import { useContext, useEffect } from 'react';
import { AuthContext } from './Authentication';
import Loader from '@/components/Loader/Loader';
import { usePathname, useRouter } from 'next/navigation';

export default function AuthGuard({ children }) {
  const { isAuthenticated, isLoading } = useContext(AuthContext);
  const pathname = usePathname();
  const router = useRouter();

  // Allow access to login, register, and other auth pages without authentication
  const publicPaths = ['/login', '/register', '/invite', '/reset-password', '/activate-user', '/review-user', '/verification-pending', '/account-disabled', '/changelog', '/talk'];
  const isPublicPath = publicPaths.some(path => pathname?.includes(path));

  // Check localStorage as fallback to prevent race condition
  // Cookie-based login API only stores userId; no token. userId alone = authenticated.
  const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
  const hasStoredAuth = !!userId;
  const actuallyAuthenticated = isAuthenticated || hasStoredAuth;

  useEffect(() => {
    // Wait for auth loading to complete
    if (isLoading) return;

    // If user is not authenticated and not on a public path, redirect to login
    if (!actuallyAuthenticated && !isPublicPath) {
      router.push('/login');
      return;
    }

    // If user is authenticated and on login page, redirect to dashboard
    if (actuallyAuthenticated && (pathname === '/v2/login' || pathname === '/login')) {
      router.push('/dashboard');
      return;
    }
  }, [isAuthenticated, isPublicPath, pathname, router, isLoading, actuallyAuthenticated]);

  // Show loading while auth state is being determined
  if (isLoading) {
    return <Loader showDelay />;
  }

  // Allow public paths to render immediately
  if (isPublicPath) {
    return children;
  }

  // If not authenticated, show loader while redirecting
  if (!actuallyAuthenticated) {
    return <Loader showDelay />;
  }

  // Authenticated — render immediately. Session/RBAC data (user profile,
  // permissions) loads in the background; consumers already handle it being
  // null/loading momentarily (see SessionContext.user, useRBAC().isLoading).
  return children;
}
